import { SxProps, Theme } from '@mui/material';
import { EntityBase } from '@provision/entity-types';
import { ReactNode } from 'react';
import { ColoredAvatar, DynamicIcon } from 'src/components';
import { StandaloneBadge } from 'src/components/StandaloneBadge/standalone-badge';
import { CustomAttributeColumnRenderer } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view/custom-view.types';
import { extractDisabledUserTextFromName } from 'src/graphql/entities/platform-user/utils';
import {
  formatServiceLevelWithCpp,
  getRowCppExpDate,
} from 'src/graphql/entities/service-level/utils';
import { AttributeValue } from 'src/pages/CustomViews/custom-views-entities-table/attribute-value';
import { CustomComponentsByAttribute } from 'src/pages/CustomViews/custom-views-entities-table/render-attribute-value';
import { ChipVisual, isTrueValue } from './mobile-card-layout';
import {
  CHIP_DOT_SIZE,
  ChipDash,
  ChipIdText,
  ChipMonoContent,
  ChipPillContent,
  ServiceLevelDot,
} from './mobile-card-list.elements';

const CHIP_ICON_SIZE_PX = 11;
const CHECK_STROKE_WIDTH = 3;

const avatarChipSx: SxProps<Theme> = { pl: '0.1875rem' };

const ChipPill = ({
  children,
  isMono = false,
  sx,
}: {
  children: ReactNode;
  isMono?: boolean;
  sx?: SxProps<Theme>;
}) => {
  const Content = isMono ? ChipMonoContent : ChipPillContent;

  return <StandaloneBadge size="chip" sx={sx} badgeContent={<Content>{children}</Content>} />;
};

export const CardChipValue = <T extends EntityBase>({
  record,
  column,
  rawValue,
  visual,
  customAttributeRenderer,
  customComponentsByAttribute,
}: {
  record: T;
  column: CustomViewAttribute;
  rawValue: unknown;
  visual: ChipVisual;
  customAttributeRenderer?: CustomAttributeColumnRenderer;
  customComponentsByAttribute?: CustomComponentsByAttribute;
}) => {
  const value = (
    <AttributeValue
      record={record}
      column={column}
      customAttributeRenderer={customAttributeRenderer}
      customComponentsByAttribute={customComponentsByAttribute}
    />
  );

  switch (visual) {
    case ChipVisual.Id:
      return <ChipIdText>{String(rawValue)}</ChipIdText>;

    case ChipVisual.Pill:
      return <ChipPill>{value}</ChipPill>;

    case ChipVisual.MonoPill:
      return <ChipPill isMono>{value}</ChipPill>;

    case ChipVisual.ServiceLevel: {
      const serviceLevel = formatServiceLevelWithCpp(
        String(rawValue),
        getRowCppExpDate(record),
      );

      return (
        <ChipPill>
          <ServiceLevelDot serviceLevel={serviceLevel} dotSize={CHIP_DOT_SIZE} />
          {serviceLevel}
        </ChipPill>
      );
    }

    case ChipVisual.Avatar: {
      const name = String(rawValue);
      const fullName = extractDisabledUserTextFromName(name);

      return (
        <ChipPill sx={avatarChipSx}>
          <ColoredAvatar name={fullName} size="tiny" sx={{ fontWeight: 700 }} />
          {`${fullName}${column.getSubTitleText?.(name) ?? ''}`}
        </ChipPill>
      );
    }

    case ChipVisual.Boolean:
      return (
        <ChipPill>
          {isTrueValue(rawValue) ? (
            <DynamicIcon
              name="check"
              library="lucide"
              size={CHIP_ICON_SIZE_PX}
              strokeWidth={CHECK_STROKE_WIDTH}
              sx={{ color: (theme) => theme.palette.custom.OV.sys.success }}
            />
          ) : (
            <ChipDash>&ndash;</ChipDash>
          )}
          {column.attributeLabel}
        </ChipPill>
      );

    case ChipVisual.Date:
      return (
        <ChipPill>
          <DynamicIcon name="calendar" library="lucide" size={CHIP_ICON_SIZE_PX} />
          {value}
        </ChipPill>
      );

    case ChipVisual.Raw:
      return value;
  }
};
